import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './CommandPalette.module.css'
import { NAV_TOP, NAV_SETTINGS } from '../lib/permissions'
import { useAuth } from '../auth/AuthContext'
import { hasPermission } from '../lib/permissions'

const NAV_ICON: Record<string, string> = {
  'lead-engine': '⚡',
  'sales-engine': '💼',
  'intelligence': '📊',
  'settings': '⚙️',
}
import { useDebounce } from '../design/responsive/useDebounce'
import { api } from '../lib/api'

interface SearchResult {
  id: string
  type: 'nav' | 'lead' | 'deal' | 'project' | 'task' | 'ticket' | 'contact' | 'action'
  title: string
  subtitle?: string
  url?: string
  action?: () => void
  icon: string
}

interface Props {
  open: boolean
  onClose: () => void
}

export default function CommandPalette({ open, onClose }: Props) {
  const { claims } = useAuth()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [selected, setSelected] = useState(0)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const debouncedQuery = useDebounce(query, 200)

  const navResults: SearchResult[] = [...NAV_TOP, NAV_SETTINGS]
    .filter(m => hasPermission(claims, m.permissionKey))
    .filter(m => !query || m.label.toLowerCase().includes(query.toLowerCase()))
    .map(m => ({
      id: `nav:${m.id}`,
      type: 'nav',
      title: m.label,
      subtitle: 'Navigate',
      url: m.path,
      icon: NAV_ICON[m.id] ?? '→',
    }))

  const quickActions: SearchResult[] = ([
    {
      id: 'action:new-deal',
      type: 'action' as const,
      title: 'New Deal',
      subtitle: 'Open deal intake form',
      url: '/app/deals',
      icon: '📋',
    },
    {
      id: 'action:dashboard',
      type: 'action' as const,
      title: 'Dashboard',
      subtitle: 'Go to home dashboard',
      url: '/app',
      icon: '🏠',
    },
  ] as SearchResult[]).filter(a => !query || a.title.toLowerCase().includes(query.toLowerCase()))

  async function fetchModuleResults(q: string) {
    if (!q || q.length < 2) return []
    const e = encodeURIComponent(q)
    const get = (url: string) => api.get(url).catch(() => null) as Promise<any>
    const [leads, deals, projects, tasks, tickets, contacts] = await Promise.all([
      get(`/api/lead-engine/leads?search=${e}&page_size=5`),
      get(`/api/deal-desk/deals?search=${e}&page_size=5`),
      get(`/api/project-mgmt/projects?search=${e}&page_size=4`),
      get(`/api/project-mgmt/tasks?search=${e}&limit=4`),
      get(`/api/project-mgmt/tickets?search=${e}&page_size=4`),
      get(`/api/crm/contacts?search=${e}&page_size=4`),
    ])
    const r: SearchResult[] = []
    for (const l of leads?.items ?? [])
      r.push({ id: `lead:${l.id}`, type: 'lead', title: l.company || l.domain || l.id,
               subtitle: `Lead · ${l.city || ''}${l.source ? ` · ${l.source}` : ''}`.trim(), url: '/app/leads', icon: '⚡' })
    for (const d of deals?.items ?? [])
      r.push({ id: `deal:${d.id}`, type: 'deal', title: d.prospect_company,
               subtitle: `Deal · ${d.stage}${d.vertical ? ` · ${d.vertical}` : ''}`, url: '/app/deals', icon: '📋' })
    for (const p of projects?.items ?? [])
      r.push({ id: `proj:${p.id}`, type: 'project', title: p.name,
               subtitle: `Project${p.code ? ` · ${p.code}` : ''}${p.client_company_name ? ` · ${p.client_company_name}` : ''}`,
               url: '/app/projects', icon: '🗂️' })
    for (const t of tasks?.items ?? [])
      r.push({ id: `task:${t.id}`, type: 'task', title: t.title,
               subtitle: `Task${t.project_name ? ` · ${t.project_name}` : ''}`, url: '/app/projects', icon: '✓' })
    for (const k of tickets?.items ?? [])
      r.push({ id: `tkt:${k.id}`, type: 'ticket', title: k.subject,
               subtitle: `Ticket${k.number ? ` · #${k.number}` : ''} · ${k.status}`, url: '/app/tickets', icon: '🎫' })
    for (const c of contacts?.items ?? []) {
      const cn = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || c.id
      r.push({ id: `contact:${c.id}`, type: 'contact', title: cn,
               subtitle: `Contact${c.company_name ? ` · ${c.company_name}` : ''}${c.title ? ` · ${c.title}` : ''}`,
               url: '/app/leads', icon: '👤' })
    }
    return r
  }

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); setSelected(0); return }
    inputRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return
    setLoading(true)
    fetchModuleResults(debouncedQuery).then(moduleResults => {
      const combined = [
        ...navResults,
        ...quickActions,
        ...moduleResults,
      ].slice(0, 24)
      setResults(combined)
      setSelected(0)
    }).finally(() => setLoading(false))
  }, [debouncedQuery, open])

  // Update non-async results immediately as query changes
  useEffect(() => {
    if (!debouncedQuery) {
      setResults([...navResults, ...quickActions].slice(0, 8))
    }
  }, [debouncedQuery, open])

  function handleSelect(result: SearchResult) {
    if (result.url) navigate(result.url)
    result.action?.()
    onClose()
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelected(s => Math.min(s + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelected(s => Math.max(s - 1, 0))
    } else if (e.key === 'Enter') {
      if (results[selected]) handleSelect(results[selected])
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  if (!open) return null

  const grouped = {
    nav: results.filter(r => r.type === 'nav'),
    action: results.filter(r => r.type === 'action'),
    lead: results.filter(r => r.type === 'lead'),
    deal: results.filter(r => r.type === 'deal'),
    project: results.filter(r => r.type === 'project'),
    task: results.filter(r => r.type === 'task'),
    ticket: results.filter(r => r.type === 'ticket'),
    contact: results.filter(r => r.type === 'contact'),
  }

  let idx = 0
  function ResultItem({ result }: { result: SearchResult }) {
    const thisIdx = idx++
    const isSelected = thisIdx === selected
    return (
      <button
        className={`${styles.result} ${isSelected ? styles.resultSelected : ''}`}
        onClick={() => handleSelect(result)}
        onMouseEnter={() => setSelected(thisIdx)}
        role="option"
        aria-selected={isSelected}
      >
        <span className={styles.resultIcon}>{result.icon}</span>
        <span className={styles.resultText}>
          <span className={styles.resultTitle}>{result.title}</span>
          {result.subtitle && <span className={styles.resultSubtitle}>{result.subtitle}</span>}
        </span>
        {isSelected && <kbd className={styles.enterHint}>↵</kbd>}
      </button>
    )
  }

  function Group({ label, items }: { label: string; items: SearchResult[] }) {
    if (!items.length) return null
    return (
      <div className={styles.group}>
        <div className={styles.groupLabel}>{label}</div>
        {items.map(r => <ResultItem key={r.id} result={r} />)}
      </div>
    )
  }

  const hasResults = results.length > 0

  return (
    <div className={styles.backdrop} onClick={onClose} aria-modal="true" role="dialog" aria-label="Command palette">
      <div className={styles.panel} onClick={e => e.stopPropagation()}>
        <div className={styles.searchRow}>
          <span className={styles.searchIcon} aria-hidden="true">⌕</span>
          <input
            ref={inputRef}
            className={styles.searchInput}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search leads, deals, projects, tasks, tickets, contacts…"
            aria-label="Search"
            aria-autocomplete="list"
            aria-controls="cp-results"
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <span className={styles.spinner} aria-hidden="true" />}
          <kbd className={styles.escHint}>Esc</kbd>
        </div>

        <div id="cp-results" role="listbox" className={styles.results}>
          {!hasResults && !loading && (
            <div className={styles.empty}>
              {query ? `No results for "${query}"` : 'Type to search across all modules'}
            </div>
          )}
          {hasResults && (
            <>
              <Group label="Navigate" items={grouped.nav} />
              <Group label="Quick actions" items={grouped.action} />
              <Group label="Leads" items={grouped.lead} />
              <Group label="Deals" items={grouped.deal} />
              <Group label="Projects" items={grouped.project} />
              <Group label="Tasks" items={grouped.task} />
              <Group label="Tickets" items={grouped.ticket} />
              <Group label="Contacts" items={grouped.contact} />
            </>
          )}
        </div>

        <div className={styles.footer}>
          <span><kbd>↑↓</kbd> navigate</span>
          <span><kbd>↵</kbd> select</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  )
}
